import { StylesProps } from 'src/library/types';
import { TmsCarrierReceivablesStyles } from './Invoices.styles';
import moment from 'moment';
import { capitalize, transformName } from 'src/utils/utils';

export const rangeFilterOptions = [
  { key: 'billing_cycle', value: 'Billing Cycle' },
  { key: 'event_date', value: 'Created At' },
];

export const getAvailableActions = (status: string) => {
  switch (status) {
    case 'created':
      return [
        {
          action_id: 'raise_invoice',
          pretty_name: 'Raise',
        },
      ];
    case 'dispute_raised':
      return [
        {
          action_id: 'resubmit_invoice',
          pretty_name: 'Resubmit',
        },
      ];
    case 'paid':
      return [
        {
          action_id: 'settle_invoice',
          pretty_name: 'Settle',
        },
        {
          action_id: 'settle_invoice_dispute',
          pretty_name: 'Settlement Dispute',
        },
      ];
    case 'settled':
      return [
        {
          action_id: 'view_settled_invoice',
          pretty_name: 'View',
        },
      ];
    default:
      return [];
  }
};

export interface IBucketList {
  [key: string]: {
    pretty_name: string;
    column_id: string;
    is_sortable: boolean;
    sort_key: string;
  }[];
}

export interface IFilterList {
  [key: string]: {
    type: 'search' | 'input';
    label: string;
    id: string;
    placeholder: string;
    mode: 'multiple';
    threshold_length: number;
  }[];
}

export interface TmsCarrierReceivablesRecord {
  id: string;
  invoice_number: string;
  carrier_type: string;
  start_date: string;
  end_date: string;
  total_amount_without_taxes: string;
  tax_amount: string;
  total_amount_with_taxes: string;
  status: string;
  created_at: string;
  raised_at: string;
  approved_at: string;
  object_type_name?: string;
  hub_name?: string;
}

export interface ITmsCarrierReceivablesRequest {
  loader: boolean;
  isNextPresent: boolean;
  last_row_id_prev: number;
  last_row_id_next: number;
  last_sorted_column_value_prev: number;
  last_sorted_column_value_next: number;
  last_sort_by_key: string;
  page_data: TmsCarrierReceivablesRecord[];
  count: {
    [key: string]: number;
  };
  filters: {
    invoiceNumberList?: string[];
    invoiceNumber?: string;
  };
  pagination: {
    resultPerPage: number;
    nextOrPrev?: string;
    pageNumber: number;
    isNextPresent?: boolean;
  };
  payoutBuckets: {
    [key: string]: string;
  };
  payoutBucketColumns: {
    [key: string]: Array<{
      pretty_name: string;
      column_id: string;
      is_sortable?: boolean;
      sort_key?: string;
      width?: number;
      fixed?: string;
      align?: string;
    }>;
  };
  invoiceBuckets: {
    [key: string]: string;
  };
  invoiceBucketColumns: {
    [key: string]: Array<{
      pretty_name: string;
      column_id: string;
      is_sortable?: boolean;
      sort_key?: string;
      width?: number;
      fixed?: string;
      align?: string;
    }>;
  };
  payoutStatusFilterOptions?: {
    [key: string]: string[];
  };
  allowVendorsToAddAdhocCharge?: boolean;
  enableGlobalAddChargeOption?: boolean;
  enableGlobalAddChargeSingleOption?: boolean;
  isCnAggregationAtTrip?: boolean;
  isVehicleLevelAggregation?: boolean;
  isGroupedView?: boolean;
  isPayoutMasterDataLoaded?: boolean;
  billingCycleType?: 'weekly' | 'monthly';
  billingCycleStart?: number;
  payoutData?: PayoutData;
  views?: {
    key: string;
    value: string;
    pretty_name?: string;
    payoutData?: Partial<PayoutData>;
    resolved?: Record<string, unknown>;
  }[];
  viewSplitConfig?: {
    resolution_key: string;
    views: Array<{
      key: string;
      value: string;
      pretty_name?: string;
    }>;
  };
  activeViewType?: string;
}

export interface TmsCarrierReceivablesProps
  extends StylesProps<ReturnType<typeof TmsCarrierReceivablesStyles>> {
  bucketList: IBucketList;
  filterList: IFilterList;
  tmsCarrierReceivables: ITmsCarrierReceivablesRequest;
  setFilters: (data: any) => void;
  setPagination: (data: any) => void;
  fetchPayoutData: (data: any) => void;
  fetchInvoiceData: (data: any) => void;
  setActiveViewType: (viewKey: string) => void;
}

export interface TmsCarrierReceivablesState {
  selectedRowKeys: React.Key[];
  searchFilterQuery: string;
  searching: boolean;
  searchFilterId: string;
  searchInvoiceNumberQuery: string;
  activeTabKey: string;
  isRaiseInvoiceActionDrawerVisible: boolean;
  isSettleInvoiceActionDrawerVisible: boolean;
  isResubmitInvoiceActionDrawerVisible: boolean;
  selectedRow: any;
  actionType: string;
  bucketFilters: {
    [key: string]: {
      dateRange: [moment.Moment, moment.Moment];
      dateFilterType: string;
      invoiceNumber: string;
    };
  };
  isReadonly: boolean;
}

export const actionTypeAndModalTitleMapping = {
  raise_invoice: 'Raise Invoice',
  resubmit_invoice: 'Resubmit Invoice',
  approve_invoice: 'Approve Invoice',
  raise_invoice_dispute: 'Raise Dispute',
  settle_invoice_dispute: 'Settle Dispute',
  settle_invoice: 'Settle Invoice',
  invoice_payment: 'Payment',
};

export const transactionColumns = [
  {
    pretty_name: 'Transaction Date',
    column_id: 'transaction_date',
  },
  {
    pretty_name: 'Transaction Ref No.',
    column_id: 'transaction_ref_no',
  },
  {
    pretty_name: 'Amount',
    column_id: 'amount',
  },
  {
    pretty_name: 'Remarks',
    column_id: 'remarks',
    width: '300px',
  },
];

export const renderParamNameCell = (text: string) => ({
  props: {
    style: {
      backgroundColor: '#fafafa',
      padding: '8px',
      textAlign: 'left',
    },
  },
  children: text ?? 'N/A',
});

const renderParamValueCell = (text: string | number | boolean) => ({
  props: {
    style: {
      padding: '8px',
      display: 'block',
      textAlign: 'right',
    },
  },
  children: String(text ?? 'N/A'),
});

export const getParameterColumns = () => [
  {
    title: 'Parameter Name',
    dataIndex: 'paramName',
    key: 'paramName',
    align: 'left',
    render: (text: string) => renderParamNameCell(text),
  },
  {
    title: 'Parameter Value',
    dataIndex: 'paramValue',
    key: 'paramValue',
    align: 'right',
    render: (text: string | number | boolean) => renderParamValueCell(text),
  },
];

export const actionTypeEnum = {
  RAISE: 'raise_invoice',
  APPROVE: 'approve_invoice',
  RAISE_DISPUTE: 'raise_invoice_dispute',
  DISPUTE_SETTLEMENT: 'settle_invoice_dispute',
  SETTLE: 'settle_invoice',
  PAYMENT: 'invoice_payment',
};

export const REFERENCE_DETAILS_COLUMNS = [
  {
    title: 'Charge Head',
    dataIndex: 'chargeId',
    key: 'chargeId',
    width: '25%',
    render: (text) => (text ? capitalize(text) : 'N/A'),
  },
  {
    title: 'Updated Value',
    dataIndex: 'updatedValue',
    key: 'updatedValue',
    width: '25%',
    align: 'right' as const,
    render: (value: number) => value ?? 'N/A',
  },
];

export const CALCULATION_DETAILS_COLUMNS = [
  {
    title: 'Charge Head',
    dataIndex: 'chargeId',
    key: 'chargeId',
    render: (text?: string) => (text ? capitalize(text) : 'N/A'),
  },
  {
    title: 'Final Amount',
    dataIndex: 'payout',
    key: 'payout',
    align: 'right',
    render: (value: number) => value ?? '0',
  },
];

export const STATUS_TYPES: Record<string, {
  label: string;
  backgroundColor: string;
  color: string;
  showInfo?: boolean;
}> = {
  created: {
    label: 'Review Pending',
    backgroundColor: '#fef6ec',
    color: '#d88030',
  },
  adhoc_created: {
    label: 'Charge Added',
    backgroundColor: '#fef6ec',
    color: '#5b3511ff',
    showInfo: true,
  },
  deleted: {
    label: 'Charge Deleted',
    backgroundColor: '#fef6ec',
    color: '#924f11',
    showInfo: true,
  },
  review_payout: {
    label: 'Reviewed',
    backgroundColor: '#f8f0fe',
    color: '#7540cd',
  },
  reviewed: {
    label: 'Reviewed',
    backgroundColor: '#f8f0fe',
    color: '#7540cd',
  },
  updated: {
    label: 'Charge Updated',
    backgroundColor: '#f8f0fe',
    color: '#7540cd',
    showInfo: true,
  },
  pending: {
    label: 'Review Pending',
    backgroundColor: '#fff7e6',
    color: '#fa8c16',
  },
  invoice_generated: {
    label: 'Invoice Generated',
    backgroundColor: '#E0F7FA',
    color: '#007C91',
  },
  approved: {
    label: 'Approved',
    backgroundColor: '#e9f8ea',
    color: '#41932d',
  },
  approve_payout: {
    label: 'Approved',
    backgroundColor: '#e9f8ea',
    color: '#41932d',
  },
  rejected: {
    label: 'Rejected',
    backgroundColor: '#F3E2DF',
    color: '#C74237',
  },
  reject_payout: {
    label: 'Rejected',
    backgroundColor: '#F3E2DF',
    color: '#C74237',
  },
  sent_for_approval: {
    label: 'Approval Pending',
    backgroundColor: '#e7eef9',
    color: '#2a5cc6',
  },
  open: {
    label: 'Open',
    backgroundColor: '#e5effa',
    color: '#1659cb',
  },
  closed: {
    label: 'Closed',
    backgroundColor: '#f0f0f0',
    color: '#666666',
  },
  sent_for_review: {
    label: 'Sent for Review',
    backgroundColor: '#e5effa',
    color: '#1659cb',
  },
};

export const getEventConfig = (eventType: string) => {
  const config = STATUS_TYPES[eventType];
  return {
    id: eventType,
    label: config?.label ?? transformName(eventType),
    backgroundColor: config?.backgroundColor ?? '#E3F2FD',
    color: config?.color ?? '#1976D2',
    showInfo: config?.showInfo ?? false,
  };
};

export interface PayoutData {
  show_calculated_value?: boolean;
  is_cn_aggregation_at_trip?: boolean;
  is_detail_level_action?: boolean;
  show_more_filters?: boolean;
  use_sent_for_approval?: boolean;
}
